import { Base64 } from 'js-base64'
import { isSetupFightEvent } from 'web/utils/type-utils'
import { joinLines } from 'web/utils/utils'
import { getBuildByPatchInfo } from './core-data'
import { cloneFight } from './data-cloner'
import { FightSetup, FullFight, PatchInfo } from './models'

const CURRENT_EXPORT_FORMAT_VERSION = 1

type ExportFormat = {
    /**
     * Defines the currently supported version of exports. Bump this if you make
     * breaking changes in the export format.
     */
    exportFormatVersion: number

    fullFight: FullFight

    /**
     * Setup doesn't have to be present. We could just be exporting a fight
     * structure.
     */
    setup?: FightSetup
}

export function createExportString(params: {
    fullFight: FullFight
    setup?: FightSetup
}) {

    const { setup, fullFight } = params

    if (fullFight.events.some(isSetupFightEvent)) {
        throw Error(joinLines([
            `Full fight contained setup fight events, this should never happen`,
            `setup fight events should only live in the setup for export.`
        ]))
    }

    const exportData: ExportFormat = {
        exportFormatVersion: CURRENT_EXPORT_FORMAT_VERSION,
        setup: setup,
        fullFight: {
            ...fullFight,
            fight: {
                ...fullFight.fight,
                name: `[IMPORTED] ${fullFight.fight.name}`
            }
        }
    }

    return `export[${Base64.encode(JSON.stringify(exportData))}]`
}

export function importSetup(params: {
    exportStr: string
    skipClone?: boolean
}): {
    fullFight: FullFight
    setup?: FightSetup
} {

    const importData = tryUnpackImportString(params.exportStr)

    assertValidExportFormatVersion(importData.exportFormatVersion)

    if (importData.setup) {
        assertValidPatchInfo(importData.setup.patchInfo)
    }

    // THIS IS A VERY VERY DANGEROUS FLAG. The consumer of this return
    // MUST NOT insert this fight into the state without taking care
    // to figure out if it has been imported for the first time, or
    // needs to overwrite an existing import.
    if (params.skipClone === true) {
        return importData
    }

    // This step is key. It ensures this function can be ran repeatedly without
    // generating duplicate IDs in the system.
    const clonedData = cloneFight({
        fullFight: importData.fullFight,
        setups: importData.setup ? [importData.setup] : []
    })

    return {
        fullFight: clonedData.clonedFullFight,
        setup: clonedData.clonedSetups[0]
    }
}

function tryUnpackImportString(importString: string): ExportFormat {
    try {

        const base64 = getBase64Part(importString)
        const importData = JSON.parse(Base64.decode(base64))

        if (importData.exportFormatVersion == null) {
            throw Error()
        }

        if (importData.fullFight == null) {
            throw Error()
        }

        return importData

    } catch (error) {
        throw Error(joinLines([
            `Unrecognizable import-data format. Make sure you pasted the correct string.`,
            `The string should look like this: export[...]`
        ]))
    }
}

function assertValidExportFormatVersion(exportFormatVersion: number) {
    if (exportFormatVersion !== CURRENT_EXPORT_FORMAT_VERSION) {
        throw Error(joinLines([
            `The string you're trying to import is using a`,
            `deprecated export-format. Ask the owner to re-export it.`
        ]))
    }
}

function assertValidPatchInfo(patchInfo: PatchInfo) {
    try {
        getBuildByPatchInfo(patchInfo)
    } catch (error) {
        throw Error(joinLines([
            `The string you're trying to import was exported using a build`,
            `that is no longer supported by the application. It cannot be imported.`
        ]))
    }
}

/**
 * Extracts the base64 part of the export string. Expecting the following
 * format: `export[base64]`
 *
 * Returns an empty string if the match doesn't find anything.
 *
 * @param string Import string
 */
function getBase64Part(string: string) {
    const matchObj = string.match(/export\[(.*)\]/)
    if (matchObj) {
        return matchObj[1]
    }

    return ''
}
